import { useUserContext } from "@contexts/UserContext/index.context";
import { Button, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import type { DeliveryFilterSchemaType } from "@schemas/DeliveryForm/DeliveryFilterSchema";
import { UserRole } from "@types_def/models/user.type";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  useFindAllDeliveryOptionsQuery,
  useFindDeliveryOptionsForPartnerQuery,
} from "@/libs/generated/hooks";

type Props = {
  userRole: UserRole;
};

function DeliveryTableServiceFilter({ userRole }: Props) {
  const { t } = useTranslation();
  const { setValue } = useFormContext<DeliveryFilterSchemaType>();
  const deliveryServices = useWatch<DeliveryFilterSchemaType, "deliveryServices">({
    name: "deliveryServices",
  });
  const { userState } = useUserContext();

  const isClient = userRole === UserRole.CLIENT;
  const orgSlug = userState?.organizationId;

  const { data: allData, isLoading: allLoading } = useFindAllDeliveryOptionsQuery(undefined, {
    enabled: !isClient,
  });
  const { data: partnerData, isLoading: partnerLoading } = useFindDeliveryOptionsForPartnerQuery(
    { organizationId: orgSlug ?? "" },
    { enabled: isClient && Boolean(orgSlug) }
  );

  const isLoading = isClient ? partnerLoading : allLoading;
  const options = isClient
    ? (partnerData?.findDeliveryOptionsForPartner ?? [])
    : (allData?.findAllDeliveryOptions ?? []);

  return (
    <>
      <Typography variant="body2" letterSpacing={2} fontWeight={600} textTransform="capitalize">
        {t("layouts.sub-headers.filters.services")}
      </Typography>
      <Divider />
      {isLoading ? (
        <CircularProgress size={20} />
      ) : (
        <Stack direction="row" sx={{ flexWrap: "wrap" }} p={1} gap={1}>
          {options.map((option) => {
            const id = String(option.id);
            const active = Boolean(deliveryServices?.[id]);
            return (
              <Button
                size="small"
                variant={active ? "contained" : "outlined"}
                key={id}
                onClick={() => setValue(`deliveryServices.${id}`, !active)}
              >
                {option.name}
              </Button>
            );
          })}
        </Stack>
      )}
    </>
  );
}

export default DeliveryTableServiceFilter;
